import React, { useState, useEffect, useCallback, useMemo } from 'react';
import * as XLSX from 'xlsx';
import { SecondaryBase } from '../Commons/SecondaryBase';
import { Confirm } from '../../Commons/Modals/Confirm';
import { useFloatMessage, MESSAGE_TYPE } from '../../Providers/FloatMessage';
import signalRService from '../../../utilities/WS/SignalRService';
import {
    GetAdminParticipantsBL,
    GetAdminConfigBL,
    GetRankingBL,
    DisqualifyParticipantBL,
    ExportParticipantsCsvBL,
    GetAdminStatsBL,
    ToggleActivePersonalNoisemeterBL,
    ResetPersonalNoisemeterDataBL,
    formatMexicoDate,
    determineScreamCategory
} from '../../../utilities/BL/PersonalNoisemeter';
import { PersonalNoisemeterConfigModal } from './PersonalNoisemeterConfigModal';
import { PersonalNoisemeterResetModal } from './PersonalNoisemeterResetModal';
import { PersonalNoisemeterSocialModal } from './PersonalNoisemeterSocialModal';
import { PersonalNoisemeterInstructionsModal } from './PersonalNoisemeterInstructionsModal';

/**
 * Tablero de Control y Mesa de Operador de Grito Mexicano (PersonalNoisemeter)
 */
export const PersonalNoisemeter = ({ getOutOfTheDynamic, roomId = null }) => {
    const { showMessage } = useFloatMessage();

    // Estados de datos
    const [participants, setParticipants] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [lastUpdated, setLastUpdated] = useState(null);

    // Métricas y KPIs
    const [kpiStats, setKpiStats] = useState({
        totalRegistros: 0,
        gritosCompletados: 0,
        recordDecibels: 0,
        recordTicket: '-',
        recordName: '-',
        promedioGeneral: 0
    });

    // Filtros y búsqueda
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all'); // all, WINNERS, COMPLETED, REGISTERED, DISQUALIFIED, RESET_BY_ADMIN
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(25);
    const [totalPages, setTotalPages] = useState(1);

    // Ordenamiento interactivo de columnas
    const [sortField, setSortField] = useState(null); // 'ticket', 'fullName', 'decibelsPeak', 'decibelsAverage', 'finalScore', 'status', 'createdAt'
    const [sortDirection, setSortDirection] = useState('desc'); // 'asc', 'desc'

    const handleSort = (field) => {
        if (sortField === field) {
            setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortField(field);
            setSortDirection(field === 'fullName' || field === 'participationTicket' || field === 'ticket' ? 'asc' : 'desc');
        }
    };

    const sortedParticipants = useMemo(() => {
        if (!sortField) {
            return participants;
        }

        return [...participants].sort((a, b) => {
            let valA, valB;
            switch (sortField) {
                case 'ticket':
                    valA = (a.participationTicket || '').toLowerCase();
                    valB = (b.participationTicket || '').toLowerCase();
                    return sortDirection === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
                case 'fullName':
                    valA = (a.fullName || '').toLowerCase();
                    valB = (b.fullName || '').toLowerCase();
                    return sortDirection === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
                case 'decibelsPeak':
                    valA = a.decibelsPeak || 0;
                    valB = b.decibelsPeak || 0;
                    return sortDirection === 'asc' ? valA - valB : valB - valA;
                case 'decibelsAverage':
                    valA = a.decibelsAverage || 0;
                    valB = b.decibelsAverage || 0;
                    return sortDirection === 'asc' ? valA - valB : valB - valA;
                case 'finalScore':
                    valA = a.finalScore || 0;
                    valB = b.finalScore || 0;
                    return sortDirection === 'asc' ? valA - valB : valB - valA;
                case 'status':
                    valA = (a.isDisqualified ? 'DISQ' : a.isArchived ? 'ARCH' : a.status || '').toLowerCase();
                    valB = (b.isDisqualified ? 'DISQ' : b.isArchived ? 'ARCH' : b.status || '').toLowerCase();
                    return sortDirection === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
                case 'createdAt':
                    valA = new Date(a.playedAt || a.createdAt || 0).getTime();
                    valB = new Date(b.playedAt || b.createdAt || 0).getTime();
                    return sortDirection === 'asc' ? valA - valB : valB - valA;
                default:
                    return 0;
            }
        });
    }, [participants, sortField, sortDirection]);

    // Configuración y Modales
    const [config, setConfig] = useState(null);
    const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);
    const [isInstructionsModalOpen, setIsInstructionsModalOpen] = useState(false);
    const [isResetModalOpen, setIsResetModalOpen] = useState(false);
    const [selectedParticipantForReset, setSelectedParticipantForReset] = useState(null);
    const [disqualifyModalData, setDisqualifyModalData] = useState({
        isOpen: false,
        participant: null,
        reason: '',
        isSubmitting: false
    });
    const [isSocialModalOpen, setIsSocialModalOpen] = useState(false);

    // Modal de Confirmación Global
    const [showConfirm, setShowConfirm] = useState(false);
    const [confirmConfig, setConfirmConfig] = useState({
        message: '',
        textYes: 'Confirmar',
        textNo: 'Cancelar',
        action: async () => ({ success: true })
    });
    const [confirmData, setConfirmData] = useState(null);

    // Secciones Desplegables Ocultas
    const [isParticipationSectionOpen, setIsParticipationSectionOpen] = useState(false);
    const [isDangerZoneSectionOpen, setIsDangerZoneSectionOpen] = useState(false);

    // Modal de Seguridad para Reinicio Total (Contraseña)
    const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
    const [passwordInput, setPasswordInput] = useState('');
    const [passwordError, setPasswordError] = useState('');

    // Estadísticas del Embudo de Conversión (HU-09)
    const [funnelStats, setFunnelStats] = useState({
        qrScan: 0,
        pageView: 0,
        formStart: 0,
        formComplete: 0,
        gameStart: 0,
        gameComplete: 0,
        conversionRates: {
            scanToPage: 0,
            pageToFormStart: 0,
            formStartToComplete: 0,
            completeToGame: 0
        },
        sourceBreakdown: {}
    });
    const [isLoadingStats, setIsLoadingStats] = useState(false);
    const [lastStatsUpdated, setLastStatsUpdated] = useState(null);

    // Función para recargar solo las estadísticas del embudo
    const loadFunnelStats = useCallback(async () => {
        setIsLoadingStats(true);
        try {
            const statsRes = await GetAdminStatsBL(roomId);
            if (statsRes.success && statsRes.data) {
                setFunnelStats(statsRes.data);
                setLastStatsUpdated(new Date().toLocaleTimeString('es-MX', { hour12: false }));
            }
        } catch (err) {
            console.error('Error al actualizar estadísticas del embudo:', err);
        } finally {
            setIsLoadingStats(false);
        }
    }, [roomId]);

    // Carga de datos de participantes, configuración, métricas y cálculo de KPIs
    const loadData = useCallback(async (showLoadingSpinner = true) => {
        if (showLoadingSpinner) setIsLoading(true);
        try {
            // 1. Obtener configuración administrativa de la sala
            const configRes = await GetAdminConfigBL(roomId);
            if (configRes.success && configRes.data) {
                setConfig(configRes.data);
            }

            // 2. Obtener participantes paginados y filtrados
            const participantsRes = await GetAdminParticipantsBL(
                roomId,
                searchTerm,
                statusFilter === 'all' ? '' : statusFilter,
                currentPage,
                pageSize
            );

            if (participantsRes.success && participantsRes.data) {
                const data = participantsRes.data;
                const items = data.items || (Array.isArray(data) ? data : []);
                setParticipants(items);
                setTotalCount(data.total !== undefined ? data.total : items.length);
                setTotalPages(data.totalPages || Math.ceil((data.total || items.length) / pageSize) || 1);
            }

            // 3. Obtener ranking general para KPIs en tiempo real
            const rankingRes = await GetRankingBL(roomId, 100);
            if (rankingRes.success && rankingRes.data) {
                const rData = rankingRes.data;
                const topLeader = rData.topRanking && rData.topRanking.length > 0 ? rData.topRanking[0] : null;

                setKpiStats(prev => ({
                    ...prev,
                    totalRegistros: rData.totalParticipants || (participantsRes.data?.total ?? prev.totalRegistros),
                    gritosCompletados: rData.totalParticipants || 0,
                    recordDecibels: rData.recordPeakDecibels || 0,
                    recordTicket: topLeader ? topLeader.ticket : '-',
                    recordName: topLeader ? topLeader.displayName : '-',
                    promedioGeneral: rData.averageScore || 0
                }));
            }

            // 4. Obtener estadísticas del embudo de telemetría
            const statsRes = await GetAdminStatsBL(roomId);
            if (statsRes.success && statsRes.data) {
                setFunnelStats(statsRes.data);
                setLastStatsUpdated(new Date().toLocaleTimeString('es-MX', { hour12: false }));
            }

            setLastUpdated(new Date().toLocaleTimeString('es-MX', { hour12: false }));
        } catch (err) {
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Error al consultar participantes y métricas del servidor.'
            });
        } finally {
            if (showLoadingSpinner) setIsLoading(false);
        }
    }, [roomId, searchTerm, statusFilter, currentPage, pageSize, showMessage]);

    // Efecto de carga inicial y cuando cambian filtros/paginación
    useEffect(() => {
        loadData(true);
    }, [loadData]);

    // Integración en tiempo real con SignalR
    useEffect(() => {
        const handleRankingUpdate = (data) => {
            // Actualización reactiva sin reload destructivo
            loadData(false);
        };

        signalRService.on('personalNoisemeter:rankingUpdate', handleRankingUpdate);

        return () => {
            signalRService.off('personalNoisemeter:rankingUpdate', handleRankingUpdate);
        };
    }, [loadData]);

    // Exportar archivo Excel profesional (2 hojas: Participantes y Estadísticas/Embudo)
    const handleExportExcel = async () => {
        setIsExporting(true);
        try {
            // 1. Obtener listado completo de participantes (todos los registros de la sala)
            const participantsRes = await GetAdminParticipantsBL(roomId, '', 'all', 1, 10000);
            const allParticipants = participantsRes.success && participantsRes.data?.items ? participantsRes.data.items : participants;

            // 2. Obtener estadísticas actualizadas del embudo
            let statsData = funnelStats;
            if (!statsData || !statsData.conversionRates) {
                const statsRes = await GetAdminStatsBL(roomId);
                if (statsRes.success && statsRes.data) {
                    statsData = statsRes.data;
                }
            }

            // --- HOJA 1: PARTICIPANTES Y RANKING ---
            const isScoreFormula = config?.enableScoreFormula || false;
            const sortedParticipants = [...allParticipants].sort((a, b) => {
                if (isScoreFormula) {
                    if ((b.finalScore || 0) !== (a.finalScore || 0)) return (b.finalScore || 0) - (a.finalScore || 0);
                    if ((b.decibelsPeak || 0) !== (a.decibelsPeak || 0)) return (b.decibelsPeak || 0) - (a.decibelsPeak || 0);
                } else {
                    if ((b.decibelsPeak || 0) !== (a.decibelsPeak || 0)) return (b.decibelsPeak || 0) - (a.decibelsPeak || 0);
                    if ((b.decibelsAverage || 0) !== (a.decibelsAverage || 0)) return (b.decibelsAverage || 0) - (a.decibelsAverage || 0);
                }
                return new Date(a.playedAt || a.createdAt) - new Date(b.playedAt || b.createdAt);
            });

            let rankPos = 0;
            const sheet1Data = sortedParticipants.map(p => {
                let posStr = '-';
                if (p.status === 'COMPLETED' && !p.isDisqualified && !p.isArchived) {
                    rankPos++;
                    posStr = rankPos.toString();
                }

                const row = {
                    'Posición': posStr,
                    'Ticket': p.participationTicket || '-',
                    'Nombre Completo': p.fullName || '-',
                    'Correo Electrónico': p.email || '-',
                    'Teléfono Celular': p.phone || '-',
                    'Decibeles Pico (dB)': p.decibelsPeak ? p.decibelsPeak.toFixed(2) : '0.00',
                    'Decibeles Promedio (dB)': p.decibelsAverage ? p.decibelsAverage.toFixed(2) : '0.00'
                };

                if (isScoreFormula) {
                    row['Score Final'] = p.finalScore ? p.finalScore.toFixed(2) : '0.00';
                }

                row['Categoría del Grito'] = p.status === 'COMPLETED' ? (p.category || determineScreamCategory(p.decibelsPeak)) : '-';
                row['Estado'] = p.isDisqualified ? 'Descalificado' : p.isArchived ? 'Reactivado' : p.status === 'COMPLETED' ? 'Completado' : 'Pendiente';
                row['¿Descalificado?'] = p.isDisqualified ? 'SÍ' : 'NO';
                row['Motivo Descalificación'] = p.disqualificationReason || '';
                row['¿Reactivado / Archivado?'] = p.isArchived ? 'SÍ' : 'NO';
                row['Motivo Reactivación'] = p.resetReason || '';
                row['# Intento'] = p.attemptNumber || 1;
                row['Fecha de Registro'] = formatMexicoDate(p.createdAt);
                row['Fecha de Juego'] = p.playedAt ? formatMexicoDate(p.playedAt) : '-';

                return row;
            });

            // --- HOJA 2: ESTADÍSTICAS Y EMBUDO ---
            const sheet2Data = [
                { 'Métrica / Indicador': '=== EMBUDO DE PARTICIPACIÓN ===', 'Valor': '', 'Detalle': '' },
                { 'Métrica / Indicador': 'Escaneos de Código QR (qr_scan)', 'Valor': statsData?.qrScan ?? 0, 'Detalle': 'Total de veces que se escaneó el QR físico o digital' },
                { 'Métrica / Indicador': 'Ingresos a la Página (page_view)', 'Valor': statsData?.pageView ?? 0, 'Detalle': 'Visitas totales a la pantalla del juego' },
                { 'Métrica / Indicador': 'Inicios de Registro (form_start)', 'Valor': statsData?.formStart ?? 0, 'Detalle': 'Personas que interactuaron con el formulario' },
                { 'Métrica / Indicador': 'Registros Completados (form_complete)', 'Valor': statsData?.formComplete ?? 0, 'Detalle': 'Aficionados registrados con ticket asignado' },
                { 'Métrica / Indicador': 'Gritos Iniciados (game_start)', 'Valor': statsData?.gameStart ?? 0, 'Detalle': 'Personas que activaron micrófono y conteo 3-2-1' },
                { 'Métrica / Indicador': 'Gritos Completados (game_complete)', 'Valor': statsData?.gameComplete ?? 0, 'Detalle': 'Gritos finalizados con puntaje oficial en ranking' },
                { 'Métrica / Indicador': '', 'Valor': '', 'Detalle': '' },
                { 'Métrica / Indicador': '=== TASAS DE CONVERSIÓN ENTRE ETAPAS ===', 'Valor': '', 'Detalle': '' },
                { 'Métrica / Indicador': 'QR ➔ Visita a Página', 'Valor': `${(statsData?.conversionRates?.scanToPage ?? 0).toFixed(1)}%`, 'Detalle': 'Efectividad de escaneo a carga de app' },
                { 'Métrica / Indicador': 'Visita ➔ Inicio de Registro', 'Valor': `${(statsData?.conversionRates?.pageToFormStart ?? 0).toFixed(1)}%`, 'Detalle': 'Porcentaje que decide registrarse' },
                { 'Métrica / Indicador': 'Inicio de Formulario ➔ Registro Completado', 'Valor': `${(statsData?.conversionRates?.formStartToComplete ?? 0).toFixed(1)}%`, 'Detalle': 'Completación del formulario de registro' },
                { 'Métrica / Indicador': 'Registro Completado ➔ Grito Finalizado', 'Valor': `${(statsData?.conversionRates?.completeToGame ?? 0).toFixed(1)}%`, 'Detalle': 'Conversión final a grito completado' },
                { 'Métrica / Indicador': '', 'Valor': '', 'Detalle': '' },
                { 'Métrica / Indicador': '=== DESGLOSE POR FUENTE DE PROCEDENCIA ===', 'Valor': '', 'Detalle': '' }
            ];

            if (statsData?.sourceBreakdown && Object.keys(statsData.sourceBreakdown).length > 0) {
                Object.entries(statsData.sourceBreakdown).forEach(([sourceName, count]) => {
                    sheet2Data.push({
                        'Métrica / Indicador': `Fuente: ${sourceName}`,
                        'Valor': count,
                        'Detalle': `Participaciones atribuidas al parámetro ?s=${sourceName}`
                    });
                });
            } else {
                sheet2Data.push({
                    'Métrica / Indicador': 'Sin fuentes registradas aún',
                    'Valor': 0,
                    'Detalle': 'Directo o sin parámetro ?s='
                });
            }

            // Construir libro de trabajo Excel con XLSX
            const workbook = XLSX.utils.book_new();
            const ws1 = XLSX.utils.json_to_sheet(sheet1Data);
            const ws2 = XLSX.utils.json_to_sheet(sheet2Data);

            ws1['!cols'] = [
                { wch: 10 }, { wch: 14 }, { wch: 28 }, { wch: 28 }, { wch: 16 },
                { wch: 18 }, { wch: 22 }, { wch: 14 }, { wch: 22 }, { wch: 16 },
                { wch: 14 }, { wch: 24 }, { wch: 20 }, { wch: 24 }, { wch: 10 },
                { wch: 22 }, { wch: 22 }
            ];

            ws2['!cols'] = [
                { wch: 45 }, { wch: 18 }, { wch: 55 }
            ];

            XLSX.utils.book_append_sheet(workbook, ws1, 'Participantes y Ranking');
            XLSX.utils.book_append_sheet(workbook, ws2, 'Estadísticas y Embudo');

            const todayStr = new Date().toISOString().slice(0, 10);
            XLSX.writeFile(workbook, `Reporte_Grito_Mexicano_${todayStr}.xlsx`);

            showMessage({
                type: MESSAGE_TYPE.SUCCESS,
                text: '¡Reporte de Excel generado y descargado exitosamente (2 hojas)!'
            });
        } catch (err) {
            console.error('Error al exportar a Excel:', err);
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Error inesperado al exportar el archivo Excel.'
            });
        } finally {
            setIsExporting(false);
        }
    };

    // Reactivar participante (Abrir modal)
    const handleOpenResetModal = (participant) => {
        setSelectedParticipantForReset(participant);
        setIsResetModalOpen(true);
    };

    // Descalificar o reincorporar participante
    const handleOpenDisqualifyModal = (participant) => {
        setDisqualifyModalData({
            isOpen: true,
            participant,
            reason: '',
            isSubmitting: false
        });
    };

    const handleConfirmDisqualification = async () => {
        const { participant, reason } = disqualifyModalData;
        if (!participant) return;

        setDisqualifyModalData(prev => ({ ...prev, isSubmitting: true }));
        try {
            const nextDisqualifiedState = !participant.isDisqualified;
            const res = await DisqualifyParticipantBL({
                participantId: participant.id,
                isDisqualified: nextDisqualifiedState,
                reason: nextDisqualifiedState ? (reason.trim() || 'Descalificado por el operador') : null
            });

            if (res.success) {
                showMessage({
                    type: MESSAGE_TYPE.SUCCESS,
                    text: nextDisqualifiedState
                        ? `Participante ${participant.fullName || participant.participationTicket} descalificado.`
                        : `Participante ${participant.fullName || participant.participationTicket} reincorporado.`
                });
                setDisqualifyModalData({ isOpen: false, participant: null, reason: '', isSubmitting: false });
                loadData(false);
            } else {
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: res.message || 'Error al actualizar estatus de descalificación.'
                });
                setDisqualifyModalData(prev => ({ ...prev, isSubmitting: false }));
            }
        } catch (err) {
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Error de red al actualizar descalificación.'
            });
            setDisqualifyModalData(prev => ({ ...prev, isSubmitting: false }));
        }
    };

    // Alternar activación de registros de la dinámica (Abrir / Pausar)
    const triggerToggleActive = (nextState) => {
        setConfirmConfig({
            message: `¿Estás seguro de que deseas ${nextState ? 'ABRIR' : 'PAUSAR'} la participación y registros en la dinámica?`,
            textYes: nextState ? 'Sí, abrir registros' : 'Sí, pausar registros',
            textNo: 'Cancelar',
            action: async () => {
                showMessage({ type: MESSAGE_TYPE.INFO, text: 'Actualizando estado de la dinámica...' });
                const res = await ToggleActivePersonalNoisemeterBL(nextState, roomId);
                if (res.success) {
                    setConfig(prev => ({ ...prev, isActive: nextState }));
                    showMessage({
                        type: MESSAGE_TYPE.SUCCESS,
                        text: `Dinámica ${nextState ? 'activada (recibiendo registros)' : 'pausada (registros cerrados)'} con éxito.`
                    });
                    return { success: true };
                } else {
                    showMessage({ type: MESSAGE_TYPE.BAD, text: res.message || 'Error al actualizar el estado de la dinámica.' });
                    return { success: false, message: res.message };
                }
            }
        });
        setShowConfirm(true);
    };

    // Reiniciar por completo la dinámica (Zona Peligrosa)
    const triggerResetDynamicData = () => {
        setConfirmConfig({
            message: '¿Estás segur@ de que deseas reiniciar la dinámica por completo? Esto eliminará de forma irreversible a todos los participantes registrados, puntuaciones, telemetría y ranking en el servidor.',
            textYes: 'Sí, reiniciar todo',
            textNo: 'Cancelar',
            action: async () => {
                showMessage({ type: MESSAGE_TYPE.INFO, text: 'Reiniciando dinámica...' });
                const res = await ResetPersonalNoisemeterDataBL(roomId);
                if (res.success) {
                    setParticipants([]);
                    setTotalCount(0);
                    setKpiStats({
                        totalRegistros: 0,
                        gritosCompletados: 0,
                        recordDecibels: 0,
                        recordTicket: '-',
                        recordName: '-',
                        promedioGeneral: 0
                    });
                    setFunnelStats({
                        qrScan: 0,
                        pageView: 0,
                        formStart: 0,
                        formComplete: 0,
                        gameStart: 0,
                        gameComplete: 0,
                        conversionRates: {
                            scanToPage: 0,
                            pageToFormStart: 0,
                            formStartToComplete: 0,
                            completeToGame: 0
                        },
                        sourceBreakdown: {}
                    });
                    setLastUpdated(new Date().toLocaleTimeString('es-MX', { hour12: false }));
                    showMessage({
                        type: MESSAGE_TYPE.SUCCESS,
                        text: '¡La dinámica ha sido reiniciada por completo!'
                    });
                    loadData(false);
                    return { success: true };
                } else {
                    showMessage({ type: MESSAGE_TYPE.BAD, text: res.message || 'Error al reiniciar los datos de la dinámica.' });
                    return { success: false, message: res.message };
                }
            }
        });
        setShowConfirm(true);
    };

    // Flujo de Seguridad en 2 Pasos para Reinicio Total
    const handleStartResetFlow = () => {
        setPasswordInput('');
        setPasswordError('');
        setIsPasswordModalOpen(true);
    };

    const handleValidatePasswordSubmit = (e) => {
        if (e) e.preventDefault();
        if (passwordInput === 'evitarborrarregistros456852') {
            setIsPasswordModalOpen(false);
            setPasswordInput('');
            setPasswordError('');
            triggerResetDynamicData();
        } else {
            setPasswordError('Contraseña incorrecta. No tienes autorización para reiniciar la dinámica.');
        }
    };

    return (
        <SecondaryBase actionToExecute={getOutOfTheDynamic}>
            <div className="container-fluid px-3 py-2 text-dark">
                {/* ENCABEZADO PRINCIPAL */}
                <div className="d-flex flex-wrap justify-content-between align-items-center mb-4 pb-3 border-bottom gap-3">
                    <div className="d-flex align-items-center gap-3">
                        <div
                            className="rounded-3 d-flex align-items-center justify-content-center text-white shadow-sm"
                            style={{ width: '48px', height: '48px', backgroundColor: '#006847' }}
                        >
                            <i className="bi bi-megaphone-fill fs-4 text-warning"></i>
                        </div>
                        <div>
                            <h2 className="h4 fw-bold mb-0 text-dark">Grito Mexicano - Tablero de Control</h2>
                            <small className="text-secondary">
                                Monitoreo en tiempo real, moderación y administración de decibeles
                                {lastUpdated && <span className="ms-2 badge bg-light text-muted border">Actualizado: {lastUpdated}</span>}
                            </small>
                        </div>
                    </div>

                    {/* Botones de Acción de Cabecera */}
                    <div className="d-flex flex-wrap align-items-center gap-2">
                        <button
                            type="button"
                            className="btn btn-outline-success fw-semibold d-flex align-items-center gap-2 shadow-sm"
                            onClick={() => setIsConfigModalOpen(true)}
                        >
                            <i className="bi bi-gear-fill"></i>
                            <span>Configuración</span>
                        </button>

                        <button
                            type="button"
                            className="btn btn-outline-dark fw-semibold d-flex align-items-center gap-2"
                            onClick={() => setIsInstructionsModalOpen(true)}
                            title="Ver código QR e instrucciones de conexión"
                        >
                            <i className="bi bi-qr-code-scan"></i>
                            <span>Ver instrucciones de conexión</span>
                        </button>

                        <button
                            type="button"
                            className="btn btn-outline-info fw-semibold d-flex align-items-center gap-2"
                            onClick={() => setIsSocialModalOpen(true)}
                            title="Ranking para compartir en redes sociales"
                        >
                            <i className="bi bi-share-fill"></i>
                            <span>Ranking para compartir</span>
                        </button>
                    </div>
                </div>

                {/* SECCIÓN: MÉTRICAS Y EMBUDO EN TIEMPO REAL CON SCROLL HORIZONTAL */}
                <div className="card shadow-sm border-0 rounded-4 p-4 bg-white mb-4">
                    <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 pb-2 border-bottom gap-2">
                        <div className="d-flex align-items-center gap-2">
                            <i className="bi bi-funnel-fill text-primary fs-5"></i>
                            <div>
                                <h5 className="mb-0 fw-bold text-dark">Estadísticas</h5>
                                <small className="text-secondary">
                                    Seguimiento en vivo del flujo de conversión y rendimiento acústico de los aficionados
                                </small>
                            </div>
                        </div>

                        <div className="d-flex align-items-center gap-2">
                            {lastStatsUpdated && (
                                <span className="badge bg-light text-muted border small">
                                    Actualizado: {lastStatsUpdated}
                                </span>
                            )}
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1"
                                onClick={() => {
                                    loadFunnelStats();
                                    loadData(false);
                                }}
                                disabled={isLoadingStats || isLoading}
                                title="Actualizar estadísticas y participantes"
                            >
                                <i className={`bi bi-arrow-repeat ${(isLoadingStats || isLoading) ? 'spin-icon' : ''}`}></i>
                                <span>{(isLoadingStats || isLoading) ? 'Cargando...' : 'Actualizar'}</span>
                            </button>
                        </div>
                    </div>

                    {/* 7 TARJETAS MÉTRICAS EN FILA CON SCROLL HORIZONTAL */}
                    <div
                        className="d-flex flex-nowrap overflow-auto gap-3 text-center py-2 px-1"
                        style={{
                            scrollbarWidth: 'thin',
                            WebkitOverflowScrolling: 'touch'
                        }}
                    >
                        {/* 1. Escaneos QR */}
                        <div style={{ flex: '0 0 170px', minWidth: '170px' }}>
                            <div className="p-3 bg-light rounded-3 border h-100 position-relative shadow-sm d-flex flex-column justify-content-center">
                                <div className="text-muted small fw-semibold text-uppercase mb-1">
                                    📱 Escaneos de QR
                                </div>
                                <div className="fs-3 fw-bold text-dark">
                                    {funnelStats?.qrScan || 0}
                                </div>
                            </div>
                        </div>

                        {/* 2. Ingresos Página */}
                        <div style={{ flex: '0 0 170px', minWidth: '170px' }}>
                            <div className="p-3 bg-light rounded-3 border h-100 position-relative shadow-sm d-flex flex-column justify-content-center">
                                <div className="text-muted small fw-semibold text-uppercase mb-1">
                                    🌐 Ingresos a página
                                </div>
                                <div className="fs-3 fw-bold text-primary">
                                    {funnelStats?.pageView || 0}
                                </div>
                            </div>
                        </div>

                        {/* 3. Inicios Registro */}
                        <div style={{ flex: '0 0 170px', minWidth: '170px' }}>
                            <div className="p-3 bg-light rounded-3 border h-100 position-relative shadow-sm d-flex flex-column justify-content-center">
                                <div className="text-muted small fw-semibold text-uppercase mb-1">
                                    ✍️ Inicios registro
                                </div>
                                <div className="fs-3 fw-bold text-info">
                                    {funnelStats?.formStart || 0}
                                </div>
                            </div>
                        </div>

                        {/* 4. Registros Completados */}
                        <div style={{ flex: '0 0 170px', minWidth: '170px' }}>
                            <div className="p-3 bg-light rounded-3 border h-100 position-relative shadow-sm d-flex flex-column justify-content-center">
                                <div className="text-muted small fw-semibold text-uppercase mb-1">
                                    ✅ Registros listos
                                </div>
                                <div className="fs-3 fw-bold text-success">
                                    {funnelStats?.formComplete || 0}
                                </div>
                            </div>
                        </div>

                        {/* 5. Gritos Iniciados */}
                        <div style={{ flex: '0 0 170px', minWidth: '170px' }}>
                            <div className="p-3 bg-light rounded-3 border h-100 position-relative shadow-sm d-flex flex-column justify-content-center">
                                <div className="text-muted small fw-semibold text-uppercase mb-1">
                                    🎙️ Gritos iniciados
                                </div>
                                <div className="fs-3 fw-bold text-warning-emphasis">
                                    {funnelStats?.gameStart || 0}
                                </div>
                            </div>
                        </div>

                        {/* 6. Gritos Completados */}
                        <div style={{ flex: '0 0 170px', minWidth: '170px' }}>
                            <div className="p-3 bg-light rounded-3 border h-100 position-relative shadow-sm d-flex flex-column justify-content-center">
                                <div className="text-muted small fw-semibold text-uppercase mb-1">
                                    🏆 Gritos finalizados
                                </div>
                                <div className="fs-3 fw-bold text-success">
                                    {funnelStats?.gameComplete || 0}
                                </div>
                            </div>
                        </div>

                        {/* 7. Récord Máximo dB */}
                        <div style={{ flex: '0 0 190px', minWidth: '190px' }}>
                            <div className="p-3 bg-light rounded-3 border h-100 position-relative shadow-sm d-flex flex-column justify-content-center">
                                <div className="text-muted small fw-semibold text-uppercase mb-1">
                                    👑 Récord Máximo dB
                                </div>
                                <div className="fs-3 fw-bold text-warning-emphasis">
                                    {kpiStats.recordDecibels > 0 ? `${kpiStats.recordDecibels.toFixed(2)} dB` : '0.00 dB'}
                                </div>
                                <small className="text-muted text-truncate d-block" title={kpiStats.recordTicket} style={{ fontSize: '0.75rem' }}>
                                    {kpiStats.recordTicket !== '-' ? `Líder: ${kpiStats.recordTicket}` : 'Sin registros'}
                                </small>
                            </div>
                        </div>
                    </div>
                </div>

                {/* FILTROS Y BUSCADOR */}
                <div className="card shadow-sm border-0 rounded-4 p-3 bg-white mb-4">
                    <div className="row g-2 align-items-center">
                        {/* Buscador Universal */}
                        <div className="col-12 col-md-6 col-lg-5">
                            <div className="input-group">
                                <span className="input-group-text bg-light border-end-0">
                                    <i className="bi bi-search text-secondary"></i>
                                </span>
                                <input
                                    type="text"
                                    className="form-control border-start-0"
                                    placeholder="Buscar por Ticket (GM-1234), Nombre, Correo o Teléfono..."
                                    value={searchTerm}
                                    onChange={(e) => {
                                        setSearchTerm(e.target.value);
                                        setCurrentPage(1);
                                    }}
                                />
                                {searchTerm && (
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary border-start-0"
                                        onClick={() => {
                                            setSearchTerm('');
                                            setCurrentPage(1);
                                        }}
                                    >
                                        <i className="bi bi-x-lg"></i>
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Filtro por Estatus */}
                        <div className="col-12 col-md-6 col-lg-4">
                            <div className="d-flex align-items-center gap-2">
                                <label className="small fw-semibold text-secondary text-nowrap">Estado:</label>
                                <select
                                    className="form-select"
                                    value={statusFilter}
                                    onChange={(e) => {
                                        setStatusFilter(e.target.value);
                                        setCurrentPage(1);
                                    }}
                                >
                                    <option value="all">Todos los registros</option>
                                    <option value="WINNERS">🏆 Ganadores (Top Ranking)</option>
                                    <option value="COMPLETED">Completados (Grito Exitoso)</option>
                                    <option value="REGISTERED">Pendientes (Registrados sin grito)</option>
                                    <option value="DISQUALIFIED">Descalificados</option>
                                    <option value="RESET_BY_ADMIN">Reactivados / Archivados</option>
                                </select>
                            </div>
                        </div>

                        {/* Contador de registros */}
                        <div className="col-12 col-lg-3 text-lg-end text-muted small">
                            Total: <strong className="text-dark">{totalCount}</strong> participantes
                        </div>
                    </div>
                </div>

                {/* TABLA DE PARTICIPANTES */}
                <div className="card shadow-sm border-0 rounded-4 overflow-hidden bg-white mb-4">
                    {/* Toolbar de la tabla con botón Exportar a Excel a la derecha */}
                    <div className="card-header bg-white p-3 px-4 d-flex justify-content-between align-items-center border-bottom">
                        <div className="d-flex align-items-center gap-2">
                            <i className="bi bi-people-fill text-dark fs-5"></i>
                            <h5 className="mb-0 fw-bold text-dark">
                                {statusFilter === 'WINNERS' ? '🏆 Tabla de Ganadores (Top Decibeles)' : 'Listado de Participantes'}
                            </h5>
                        </div>

                        <button
                            type="button"
                            className="btn btn-sm btn-outline-success fw-semibold d-flex align-items-center gap-2 shadow-sm"
                            onClick={handleExportExcel}
                            disabled={isExporting}
                            title="Exportar todos los registros a hoja de cálculo Excel"
                        >
                            {isExporting ? (
                                <span className="spinner-border spinner-border-sm" role="status"></span>
                            ) : (
                                <i className="bi bi-file-earmark-excel-fill text-success"></i>
                            )}
                            <span>Exportar a excel</span>
                        </button>
                    </div>

                    <div className="table-responsive" style={{ minHeight: '320px' }}>
                        <table className="table align-middle table-hover mb-0">
                            <thead className="table-light">
                                <tr className="small text-secondary fw-bold text-uppercase">
                                    <th scope="col" className="text-center cursor-pointer user-select-none" style={{ width: '70px', cursor: 'pointer' }} onClick={() => handleSort('position')}>
                                        <div className="d-flex align-items-center justify-content-center gap-1">
                                            <span>Pos</span>
                                            <i className={`bi ${sortField === 'position' ? (sortDirection === 'asc' ? 'bi-sort-up text-primary' : 'bi-sort-down text-primary') : 'bi-arrow-down-up text-muted opacity-50'}`} style={{ fontSize: '0.75rem' }}></i>
                                        </div>
                                    </th>
                                    <th scope="col" className="cursor-pointer user-select-none" style={{ width: '120px', cursor: 'pointer' }} onClick={() => handleSort('ticket')}>
                                        <div className="d-flex align-items-center gap-1">
                                            <span>Ticket</span>
                                            <i className={`bi ${sortField === 'ticket' ? (sortDirection === 'asc' ? 'bi-sort-up text-primary' : 'bi-sort-down text-primary') : 'bi-arrow-down-up text-muted opacity-50'}`} style={{ fontSize: '0.75rem' }}></i>
                                        </div>
                                    </th>
                                    <th scope="col" className="cursor-pointer user-select-none" style={{ cursor: 'pointer' }} onClick={() => handleSort('fullName')}>
                                        <div className="d-flex align-items-center gap-1">
                                            <span>Participante</span>
                                            <i className={`bi ${sortField === 'fullName' ? (sortDirection === 'asc' ? 'bi-sort-up text-primary' : 'bi-sort-down text-primary') : 'bi-arrow-down-up text-muted opacity-50'}`} style={{ fontSize: '0.75rem' }}></i>
                                        </div>
                                    </th>
                                    <th scope="col">Contacto</th>
                                    <th scope="col" className="text-center cursor-pointer user-select-none" style={{ cursor: 'pointer' }} onClick={() => handleSort('decibelsPeak')}>
                                        <div className="d-flex align-items-center justify-content-center gap-1">
                                            <span>dB Pico</span>
                                            <i className={`bi ${sortField === 'decibelsPeak' ? (sortDirection === 'asc' ? 'bi-sort-up text-primary' : 'bi-sort-down text-primary') : 'bi-arrow-down-up text-muted opacity-50'}`} style={{ fontSize: '0.75rem' }}></i>
                                        </div>
                                    </th>
                                    <th scope="col" className="text-center cursor-pointer user-select-none" style={{ cursor: 'pointer' }} onClick={() => handleSort('decibelsAverage')}>
                                        <div className="d-flex align-items-center justify-content-center gap-1">
                                            <span>dB Promedio</span>
                                            <i className={`bi ${sortField === 'decibelsAverage' ? (sortDirection === 'asc' ? 'bi-sort-up text-primary' : 'bi-sort-down text-primary') : 'bi-arrow-down-up text-muted opacity-50'}`} style={{ fontSize: '0.75rem' }}></i>
                                        </div>
                                    </th>
                                    {config?.enableScoreFormula ? (
                                        <th scope="col" className="text-center cursor-pointer user-select-none" style={{ cursor: 'pointer' }} onClick={() => handleSort('finalScore')}>
                                            <div className="d-flex align-items-center justify-content-center gap-1">
                                                <span>Score Final</span>
                                                <i className={`bi ${sortField === 'finalScore' ? (sortDirection === 'asc' ? 'bi-sort-up text-primary' : 'bi-sort-down text-primary') : 'bi-arrow-down-up text-muted opacity-50'}`} style={{ fontSize: '0.75rem' }}></i>
                                            </div>
                                        </th>
                                    ) : (
                                        <th scope="col" className="text-center">
                                            <span>Categoría</span>
                                        </th>
                                    )}
                                    <th scope="col" className="text-center cursor-pointer user-select-none" style={{ cursor: 'pointer' }} onClick={() => handleSort('status')}>
                                        <div className="d-flex align-items-center justify-content-center gap-1">
                                            <span>Estado</span>
                                            <i className={`bi ${sortField === 'status' ? (sortDirection === 'asc' ? 'bi-sort-up text-primary' : 'bi-sort-down text-primary') : 'bi-arrow-down-up text-muted opacity-50'}`} style={{ fontSize: '0.75rem' }}></i>
                                        </div>
                                    </th>
                                    <th scope="col" className="cursor-pointer user-select-none" style={{ cursor: 'pointer' }} onClick={() => handleSort('createdAt')}>
                                        <div className="d-flex align-items-center gap-1">
                                            <span>Fecha</span>
                                            <i className={`bi ${sortField === 'createdAt' ? (sortDirection === 'asc' ? 'bi-sort-up text-primary' : 'bi-sort-down text-primary') : 'bi-arrow-down-up text-muted opacity-50'}`} style={{ fontSize: '0.75rem' }}></i>
                                        </div>
                                    </th>
                                    <th scope="col" className="text-center" style={{ width: '140px' }}>Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {isLoading ? (
                                    <tr>
                                        <td colSpan="10" className="text-center py-5">
                                            <div className="spinner-border text-success" role="status">
                                                <span className="visually-hidden">Cargando...</span>
                                            </div>
                                            <p className="text-secondary small mt-2 mb-0">Cargando participantes...</p>
                                        </td>
                                    </tr>
                                ) : sortedParticipants.length === 0 ? (
                                    <tr>
                                        <td colSpan="10" className="text-center py-5 text-secondary">
                                            <i className="bi bi-inbox fs-1 d-block text-muted mb-2"></i>
                                            <p className="fs-6 fw-bold mb-1">No se encontraron participantes</p>
                                            <p className="small text-muted mb-0">Intenta cambiar los términos de búsqueda o filtros aplicados.</p>
                                        </td>
                                    </tr>
                                ) : (
                                    sortedParticipants.map((p, index) => {
                                        const globalIndex = (currentPage - 1) * pageSize + index + 1;
                                        const isWinnersMode = statusFilter === 'WINNERS';
                                        const isWinnerPodium = isWinnersMode && globalIndex <= 3 && p.status === 'COMPLETED' && !p.isDisqualified && !p.isArchived;
                                        const podiumBadge = isWinnerPodium ? (globalIndex === 1 ? '🥇' : globalIndex === 2 ? '🥈' : '🥉') : null;

                                        return (
                                            <tr
                                                key={p.id}
                                                className={
                                                    p.isDisqualified
                                                        ? 'table-danger bg-opacity-25'
                                                        : p.isArchived
                                                        ? 'opacity-50'
                                                        : isWinnerPodium
                                                        ? 'fw-semibold'
                                                        : ''
                                                }
                                            >
                                                {/* Posición */}
                                                <td className="text-center fw-bold">
                                                    {isWinnersMode ? (
                                                        podiumBadge ? (
                                                            <span className="fs-5">{podiumBadge}</span>
                                                        ) : (
                                                            <span>#{globalIndex}</span>
                                                        )
                                                    ) : p.status === 'COMPLETED' && !p.isDisqualified && !p.isArchived ? (
                                                        <span className="text-muted">#{globalIndex}</span>
                                                    ) : (
                                                        <span className="text-muted">-</span>
                                                    )}
                                                </td>

                                                {/* Ticket */}
                                                <td>
                                                    <span className="badge bg-dark font-monospace px-2 py-1 fs-6">
                                                        {p.participationTicket}
                                                    </span>
                                                </td>

                                                {/* Nombre */}
                                                <td>
                                                    <div className="fw-bold text-dark">{p.fullName}</div>
                                                    {p.attemptNumber > 1 && (
                                                        <span className="badge bg-info text-dark rounded-pill" style={{ fontSize: '0.65rem' }}>
                                                             Reintento #{p.attemptNumber}
                                                        </span>
                                                    )}
                                                    {p.isDisqualified && (
                                                        <span className="badge bg-danger rounded-pill ms-1" style={{ fontSize: '0.65rem' }}>
                                                            Descalificado
                                                        </span>
                                                    )}
                                                </td>

                                                {/* Contacto */}
                                                <td>
                                                    <div className="small text-secondary text-truncate" style={{ maxWidth: '180px' }}>
                                                        <i className="bi bi-envelope me-1"></i>{p.email || 'N/A'}
                                                    </div>
                                                    <div className="small text-secondary">
                                                        <i className="bi bi-telephone me-1"></i>{p.phone || 'N/A'}
                                                    </div>
                                                </td>

                                                {/* dB Pico */}
                                                <td className="text-center font-monospace fw-semibold text-danger">
                                                    {p.decibelsPeak > 0 ? `${p.decibelsPeak.toFixed(2)} dB` : '-'}
                                                </td>

                                                {/* dB Promedio */}
                                                <td className="text-center font-monospace fw-semibold text-secondary">
                                                    {p.decibelsAverage > 0 ? `${p.decibelsAverage.toFixed(2)} dB` : '-'}
                                                </td>

                                                {/* Score Final / Categoría */}
                                                {config?.enableScoreFormula ? (
                                                    <td className="text-center">
                                                        {p.finalScore > 0 ? (
                                                            <div>
                                                                <div className="fw-bold fs-6 text-success font-monospace">
                                                                    {p.finalScore.toFixed(2)} pts
                                                                </div>
                                                                <span
                                                                    className="badge bg-light text-dark border small"
                                                                    style={{ fontSize: '0.65rem' }}
                                                                >
                                                                    {determineScreamCategory(p.decibelsPeak)}
                                                                </span>
                                                            </div>
                                                        ) : (
                                                            <span className="text-muted small">Sin score</span>
                                                        )}
                                                    </td>
                                                ) : (
                                                    <td className="text-center">
                                                        {p.status === 'COMPLETED' ? (
                                                            <span
                                                                className="badge bg-light text-dark border small"
                                                                style={{ fontSize: '0.75rem' }}
                                                            >
                                                                {p.category || determineScreamCategory(p.decibelsPeak)}
                                                            </span>
                                                        ) : (
                                                            <span className="text-muted small">-</span>
                                                        )}
                                                    </td>
                                                )}

                                                {/* Estado */}
                                                <td className="text-center">
                                                    {p.isDisqualified ? (
                                                        <span className="badge bg-danger">Descalificado</span>
                                                    ) : p.isArchived ? (
                                                        <span className="badge bg-secondary" title={p.resetReason || 'Archivado por operador'}>
                                                            Reactivado
                                                        </span>
                                                    ) : p.status === 'COMPLETED' ? (
                                                        <span className="badge bg-success">Completado</span>
                                                    ) : (
                                                        <span className="badge bg-warning text-dark">Pendiente</span>
                                                    )}
                                                </td>

                                                {/* Fecha */}
                                                <td className="small text-muted text-nowrap">
                                                    {formatMexicoDate(p.playedAt || p.createdAt)}
                                                </td>

                                                {/* Acciones */}
                                                <td className="text-center">
                                                    <div className="d-flex justify-content-center gap-1">
                                                        {/* Botón Reactivar */}
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-outline-warning text-dark py-1 px-2"
                                                            title="Reactivar y permitir nuevo intento"
                                                            onClick={() => handleOpenResetModal(p)}
                                                        >
                                                            <i className="bi bi-arrow-counterclockwise"></i>
                                                        </button>

                                                        {/* Botón Descalificar / Reincorporar */}
                                                        <button
                                                            type="button"
                                                            className={`btn btn-sm py-1 px-2 ${
                                                                p.isDisqualified ? 'btn-outline-success' : 'btn-outline-danger'
                                                            }`}
                                                            title={p.isDisqualified ? 'Reincorporar al ranking' : 'Descalificar participante'}
                                                            onClick={() => handleOpenDisqualifyModal(p)}
                                                        >
                                                            <i className={`bi ${p.isDisqualified ? 'bi-check-circle' : 'bi-ban'}`}></i>
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* PAGINACIÓN */}
                    <div className="card-footer bg-light p-3 d-flex flex-wrap justify-content-between align-items-center gap-2 border-top">
                        <div className="small text-secondary">
                            Mostrando {(currentPage - 1) * pageSize + 1} - {Math.min(currentPage * pageSize, totalCount)} de {totalCount} participantes
                        </div>

                        <div className="d-flex align-items-center gap-2">
                            <select
                                className="form-select form-select-sm"
                                style={{ width: '80px' }}
                                value={pageSize}
                                onChange={(e) => {
                                    setPageSize(Number(e.target.value));
                                    setCurrentPage(1);
                                }}
                            >
                                <option value="10">10</option>
                                <option value="25">25</option>
                                <option value="50">50</option>
                                <option value="100">100</option>
                            </select>

                            <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary"
                                disabled={currentPage <= 1 || isLoading}
                                onClick={() => setCurrentPage(1)}
                            >
                                <i className="bi bi-chevron-double-left"></i>
                            </button>
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary"
                                disabled={currentPage <= 1 || isLoading}
                                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                            >
                                <i className="bi bi-chevron-left"></i>
                            </button>

                            <span className="small fw-semibold text-dark px-2">
                                Página {currentPage} de {totalPages || 1}
                            </span>

                            <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary"
                                disabled={currentPage >= totalPages || isLoading}
                                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                            >
                                <i className="bi bi-chevron-right"></i>
                            </button>
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary"
                                disabled={currentPage >= totalPages || isLoading}
                                onClick={() => setCurrentPage(totalPages)}
                            >
                                <i className="bi bi-chevron-double-right"></i>
                            </button>
                        </div>
                    </div>
                </div>

                {/* 1. SECCIÓN DESPLEGABLE OCULTA: CONTROL DE PARTICIPACIÓN Y REGISTROS */}
                <div className="card shadow-sm border-0 rounded-4 overflow-hidden mb-3 bg-white">
                    <div
                        className="card-header bg-white p-3 px-4 d-flex justify-content-between align-items-center cursor-pointer user-select-none"
                        style={{ cursor: 'pointer' }}
                        onClick={() => setIsParticipationSectionOpen(prev => !prev)}
                    >
                        <div className="d-flex align-items-center gap-2">
                            <i className="bi bi-sliders text-primary fs-5"></i>
                            <div>
                                <h6 className="mb-0 fw-bold text-dark">Control de Participación y Registros</h6>
                                <small className="text-secondary">Abrir o pausar el acceso al registro de aficionados</small>
                            </div>
                        </div>
                        <div className="d-flex align-items-center gap-3">
                            <span className={`badge ${config?.isActive ? 'bg-success-subtle text-success border border-success' : 'bg-danger-subtle text-danger border border-danger'}`}>
                                {config?.isActive ? 'Registros Abiertos' : 'Registros Pausados'}
                            </span>
                            <i className={`bi ${isParticipationSectionOpen ? 'bi-chevron-up' : 'bi-chevron-down'} text-secondary fs-6`}></i>
                        </div>
                    </div>

                    {isParticipationSectionOpen && (
                        <div className="card-body p-4 border-top bg-light-subtle">
                            <p className="text-secondary small mb-3">
                                Estatus actual: <span className={`fw-bold ${config?.isActive ? 'text-success' : 'text-danger'}`}>
                                    {config?.isActive ? 'Dinámica Activa (Recibiendo registros de aficionados)' : 'Dinámica Pausada (Registro y participación bloqueados)'}
                                </span>
                            </p>
                            <button
                                type="button"
                                onClick={() => triggerToggleActive(!config?.isActive)}
                                className={`btn w-100 py-3 fw-bold d-flex align-items-center justify-content-center gap-2 transition-all ${
                                    config?.isActive 
                                    ? 'btn-outline-danger' 
                                    : 'btn-outline-success'
                                }`}
                            >
                                <i className={`bi ${config?.isActive ? 'bi-pause-circle-fill' : 'bi-play-circle-fill'}`}></i>
                                {config?.isActive ? 'Pausar dinámica (Bloquear registros)' : 'Activar dinámica (Abrir registros)'}
                            </button>
                        </div>
                    )}
                </div>

                {/* 2. SECCIÓN DESPLEGABLE OCULTA: ZONA PELIGROSA */}
                <div className="card shadow-sm border border-danger border-opacity-25 rounded-4 overflow-hidden mb-4 bg-white">
                    <div
                        className="card-header bg-danger-subtle p-3 px-4 d-flex justify-content-between align-items-center cursor-pointer user-select-none"
                        style={{ cursor: 'pointer' }}
                        onClick={() => setIsDangerZoneSectionOpen(prev => !prev)}
                    >
                        <div className="d-flex align-items-center gap-2">
                            <i className="bi bi-exclamation-triangle-fill text-danger fs-5"></i>
                            <div>
                                <h6 className="mb-0 fw-bold text-danger">Zona Peligrosa / Mantenimiento</h6>
                                <small className="text-danger-emphasis">Reinicio completo y eliminación definitiva de datos</small>
                            </div>
                        </div>
                        <div className="d-flex align-items-center gap-3">
                            <span className="badge bg-danger text-white">Acción Destructiva</span>
                            <i className={`bi ${isDangerZoneSectionOpen ? 'bi-chevron-up' : 'bi-chevron-down'} text-danger fs-6`}></i>
                        </div>
                    </div>

                    {isDangerZoneSectionOpen && (
                        <div className="card-body p-4 border-top bg-white">
                            <p className="small text-danger-emphasis mb-3">
                                Reiniciar la dinámica eliminará permanentemente todos los datos de los participantes registrados, puntuaciones, telemetría y registros de auditoría en el servidor de forma irreversible.
                            </p>
                            <button
                                type="button"
                                onClick={handleStartResetFlow}
                                className="btn btn-danger w-100 py-2.5 fw-bold d-flex align-items-center justify-content-center gap-2"
                            >
                                <i className="bi bi-trash-fill"></i>
                                Reiniciar dinámica por completo
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* MODAL DE INSTRUCCIONES Y CÓDIGO QR */}
            <PersonalNoisemeterInstructionsModal
                isOpen={isInstructionsModalOpen}
                onClose={() => setIsInstructionsModalOpen(false)}
                roomId={roomId}
                config={config}
            />

            {/* MODAL DE CONFIGURACIÓN */}
            <PersonalNoisemeterConfigModal
                isOpen={isConfigModalOpen}
                onClose={() => setIsConfigModalOpen(false)}
                roomId={roomId}
                onConfigUpdated={() => loadData(false)}
            />

            {/* MODAL DE REACTIVACIÓN (RESET) */}
            <PersonalNoisemeterResetModal
                isOpen={isResetModalOpen}
                onClose={() => {
                    setIsResetModalOpen(false);
                    setSelectedParticipantForReset(null);
                }}
                participant={selectedParticipantForReset}
                enableScoreFormula={config?.enableScoreFormula}
                onResetSuccess={() => loadData(false)}
            />

            {/* MODAL DE CONFIRMACIÓN DE DESCALIFICACIÓN */}
            {disqualifyModalData.isOpen && (
                <>
                    <div
                        onClick={() => setDisqualifyModalData({ isOpen: false, participant: null, reason: '', isSubmitting: false })}
                        style={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            width: '100vw',
                            height: '100vh',
                            backgroundColor: 'rgba(0, 0, 0, 0.75)',
                            zIndex: 99998,
                            backdropFilter: 'blur(4px)'
                        }}
                    />
                    <div
                        style={{
                            position: 'fixed',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: '95%',
                            maxWidth: '480px',
                            zIndex: 99999
                        }}
                    >
                        <div className="card shadow-lg border-0 rounded-4 overflow-hidden text-dark bg-white">
                            <div className={`p-3 px-4 d-flex justify-content-between align-items-center text-white ${
                                disqualifyModalData.participant?.isDisqualified ? 'bg-success' : 'bg-danger'
                            }`}>
                                <h5 className="mb-0 fw-bold">
                                    {disqualifyModalData.participant?.isDisqualified ? 'Reincorporar Participante' : 'Descalificar Participante'}
                                </h5>
                                <button
                                    type="button"
                                    className="btn-close btn-close-white"
                                    onClick={() => setDisqualifyModalData({ isOpen: false, participant: null, reason: '', isSubmitting: false })}
                                    disabled={disqualifyModalData.isSubmitting}
                                ></button>
                            </div>

                            <div className="card-body p-4">
                                <p className="mb-2">
                                    ¿Estás seguro de que deseas {disqualifyModalData.participant?.isDisqualified ? 'reincorporar' : 'descalificar'} a{' '}
                                    <strong>{disqualifyModalData.participant?.fullName}</strong> ({disqualifyModalData.participant?.participationTicket})?
                                </p>
                                {!disqualifyModalData.participant?.isDisqualified && (
                                    <div className="mt-3">
                                        <label className="form-label small fw-semibold text-secondary">
                                            Motivo de la descalificación (opcional):
                                        </label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Ej. Uso de silbato, interferencia acústica..."
                                            value={disqualifyModalData.reason}
                                            onChange={(e) => setDisqualifyModalData(prev => ({ ...prev, reason: e.target.value }))}
                                        />
                                    </div>
                                )}
                            </div>

                            <div className="card-footer bg-light p-3 px-4 d-flex justify-content-end gap-2 border-top">
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary px-3"
                                    onClick={() => setDisqualifyModalData({ isOpen: false, participant: null, reason: '', isSubmitting: false })}
                                    disabled={disqualifyModalData.isSubmitting}
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="button"
                                    className={`btn px-4 fw-bold text-white ${
                                        disqualifyModalData.participant?.isDisqualified ? 'btn-success' : 'btn-danger'
                                    }`}
                                    onClick={handleConfirmDisqualification}
                                    disabled={disqualifyModalData.isSubmitting}
                                >
                                    {disqualifyModalData.isSubmitting ? (
                                        <span className="spinner-border spinner-border-sm" role="status"></span>
                                    ) : (
                                        <span>Confirmar</span>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </>
            )}

            {/* MODAL DE SEGURIDAD (SOLICITUD DE CONTRASEÑA PARA RESET) */}
            {isPasswordModalOpen && (
                <>
                    <div
                        onClick={() => {
                            setIsPasswordModalOpen(false);
                            setPasswordInput('');
                            setPasswordError('');
                        }}
                        style={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            width: '100vw',
                            height: '100vh',
                            backgroundColor: 'rgba(0, 0, 0, 0.75)',
                            zIndex: 99998,
                            backdropFilter: 'blur(4px)'
                        }}
                    />
                    <div
                        style={{
                            position: 'fixed',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: '95%',
                            maxWidth: '440px',
                            zIndex: 99999
                        }}
                    >
                        <div className="card shadow-lg border-0 rounded-4 overflow-hidden text-dark bg-white">
                            <div className="bg-danger p-3 px-4 d-flex justify-content-between align-items-center text-white">
                                <h5 className="mb-0 fw-bold d-flex align-items-center gap-2">
                                    <i className="bi bi-shield-lock-fill"></i>
                                    <span>Clave de Autorización</span>
                                </h5>
                                <button
                                    type="button"
                                    className="btn-close btn-close-white"
                                    onClick={() => {
                                        setIsPasswordModalOpen(false);
                                        setPasswordInput('');
                                        setPasswordError('');
                                    }}
                                ></button>
                            </div>

                            <form onSubmit={handleValidatePasswordSubmit}>
                                <div className="card-body p-4">
                                    <p className="small text-secondary mb-3">
                                        Esta acción borrará permanentemente todos los datos de la sala. Ingresa la contraseña de autorización para continuar:
                                    </p>
                                    <div className="mb-3">
                                        <label className="form-label small fw-semibold text-dark">
                                            Contraseña de seguridad:
                                        </label>
                                        <input
                                            type="password"
                                            className={`form-control ${passwordError ? 'is-invalid' : ''}`}
                                            placeholder="Ingresa la contraseña..."
                                            value={passwordInput}
                                            onChange={(e) => {
                                                setPasswordInput(e.target.value);
                                                if (passwordError) setPasswordError('');
                                            }}
                                            autoFocus
                                        />
                                        {passwordError && (
                                            <div className="invalid-feedback d-block mt-2 fw-semibold">
                                                {passwordError}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="card-footer bg-light p-3 px-4 d-flex justify-content-end gap-2 border-top">
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary px-3"
                                        onClick={() => {
                                            setIsPasswordModalOpen(false);
                                            setPasswordInput('');
                                            setPasswordError('');
                                        }}
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        type="submit"
                                        className="btn btn-danger px-4 fw-bold text-white d-flex align-items-center gap-2"
                                    >
                                        <i className="bi bi-key-fill"></i>
                                        <span>Validar</span>
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </>
            )}

            {/* MODAL DE COMPARTICIÓN SOCIAL (HU-04) */}
            <PersonalNoisemeterSocialModal
                isOpen={isSocialModalOpen}
                onClose={() => setIsSocialModalOpen(false)}
                ranking={participants}
                kpiStats={kpiStats}
                config={config}
                roomId={roomId}
            />

            {/* MODAL DE CONFIRMACIÓN REUTILIZABLE */}
            {showConfirm && (
                <Confirm
                    message={confirmConfig.message}
                    actionToYesButton={confirmConfig.action}
                    textYesButton={confirmConfig.textYes}
                    textNoButton={confirmConfig.textNo}
                    setShowModal={setShowConfirm}
                    setConfirmData={setConfirmData}
                />
            )}
        </SecondaryBase>
    );
};

export const PersonalNoisemeterBoard = PersonalNoisemeter;
export default PersonalNoisemeter;
